import { useState } from "react";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../css/styles.css";
import { auth } from "../firebase";
import { Transaction, TransactionCategory } from "../types/budgethelper";
import { useCreateTransaction } from "./hooks/useCreateTransaction";
import { useGetTransactions } from "./hooks/useGetTransaction";
import { useUpdateTransaction } from "./hooks/useUpdateTransaction";
import { useDeleteTransaction } from "./hooks/useDeleteTransaction";
import { useBudgetGroupUsers } from "./hooks/useBudgetGroupUsers";
import { useUser } from "../auth/userContext";
import { Timestamp } from "firebase/firestore";
import { FilterState, TimePeriod, getTimePeriodLabel } from "../types/filterTypes";
import { TransactionList } from "./components/TransactionList";
import { TransactionModal } from "./components/TransactionModal";
import { SettlementCard } from "./components/SettlementCard";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { calculateBalancesForGroup } from "./utils/settlementCalculator";
import { validateSplitAmounts, normalizeSplitAmounts } from "./utils/validation";
import { useTransactionForm } from "./hooks/useTransactionForm";
import { useTransactionFilters } from "./hooks/useTransactionFilters";
import { ERROR_MESSAGES } from "./constants";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';


export const ExpenseTracker = () => {
  const { userProfile, budgetGroups, loading } = useUser();
  const { createTransaction } = useCreateTransaction();
  const { updateTransaction } = useUpdateTransaction();
  const { deleteTransaction } = useDeleteTransaction();
  const { transactions } = useGetTransactions();
  const { getUserFriendlyName, getBudgetGroupName, budgetGroupUsers } = useBudgetGroupUsers();

  const navigate = useNavigate();

  // Use custom hooks
  const transactionForm = useTransactionForm({
    userProfileUserId: userProfile?.userId || "",
    defaultBudgetGroup: userProfile?.defaultBudgetGroup || "",
  });

  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Transaction | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({
    selectedCategory: null,
    selectedBudgetGroups: new Set<string>(),
    timePeriod: TimePeriod.THIS_MONTH
  });

  // Use filter hook
  const { filteredTransactions, groupedTransactions, totalSpending } = useTransactionFilters(
    transactions,
    filterState
  );

  if (!userProfile || loading) return <p>Please Wait I'm Loading...</p>;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const amount = parseFloat(transactionForm.transactionAmount);
    
    if (!amount || !userProfile) return;

    // Normalize and validate split amounts
    const normalizedSplits = normalizeSplitAmounts(transactionForm.splitAmounts);
    const currentUsers = budgetGroupUsers[transactionForm.selectedBudgetGroupId] || [];
    
    if (!validateSplitAmounts(transactionForm.splitAmounts, amount, currentUsers.length)) {
      alert(ERROR_MESSAGES.SPLIT_MISMATCH);
      return;
    }

    try {
      if (transactionForm.editingTransaction?.id) {
        await updateTransaction(transactionForm.editingTransaction.id, {
          amount,
          shopName: transactionForm.shopName,
          category: transactionForm.selectedCategory as string,
          source: userProfile.userId,
          paidBy: transactionForm.paidBy,
          budgetGroup: transactionForm.selectedBudgetGroupId,
          splitAmounts: Object.keys(normalizedSplits).length > 0 ? normalizedSplits : undefined,
        });
      } else {
        await createTransaction({
          userId: userProfile.userId,
          timestamp: Timestamp.now(),
          amount,
          shopName: transactionForm.shopName,
          source: userProfile.userId,
          paidBy: transactionForm.paidBy,
          category: transactionForm.selectedCategory as string,
          budgetGroup: transactionForm.selectedBudgetGroupId,
          splitAmounts: Object.keys(normalizedSplits).length > 0 ? normalizedSplits : undefined,
        });
      }

      transactionForm.resetForm();
      setShowModal(false);
    } catch (error) {
      console.error("Failed to save transaction:", error);
      alert("Failed to save transaction. Please try again.");
    }
  };

  const handleEdit = (transaction: Transaction) => {
    transactionForm.loadTransaction(transaction);
    setShowModal(true);
  };

  const handleDelete = async (transaction: Transaction) => {
    if (!transaction.id) return;
    
    try {
      await deleteTransaction(transaction.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete transaction:", error);
      alert("Failed to delete transaction. Please try again.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    transactionForm.resetForm();
  };

  const signUserOut = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Failed to sign out:", err);
    }
  };

  // Calculate net balances when exactly one budget group is selected or user belongs to only one group
  const calculateSettlements = () => {
    if (budgetGroups.length === 1) {
      const selectedGroupId = budgetGroups[0].id;
      return calculateBalancesForGroup(filteredTransactions, selectedGroupId);
    }
    if (filterState.selectedBudgetGroups.size === 1) {
      const selectedGroupId = Array.from(filterState.selectedBudgetGroups)[0];
      return calculateBalancesForGroup(filteredTransactions, selectedGroupId);
    }
    return null;
  };

  const settlementData = calculateSettlements();

  // Check if filters are active (excluding default THIS_MONTH)
  const isFilterActive = filterState.selectedCategory !== null || 
                        filterState.selectedBudgetGroups.size > 0 || 
                        filterState.timePeriod !== TimePeriod.THIS_MONTH;
  
  const activeFilterCount = 
    (filterState.selectedCategory !== null ? 1 : 0) +
    (filterState.selectedBudgetGroups.size > 0 ? 1 : 0) +
    (filterState.timePeriod !== TimePeriod.THIS_MONTH ? 1 : 0);

  return (
    <div className="expense-tracker-modern">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div>
            <h1 className="app-title">SampleNotePad</h1>
          </div>
          <div className="header-actions">
            <button 
              className="icon-btn filter-btn" 
              onClick={() => setShowFilters(!showFilters)}
            >
              {isFilterActive ? <FilterAltIcon /> : <FilterListIcon />}
              {activeFilterCount > 0 && (
                <span className="filter-badge">{activeFilterCount}</span>
              )}
            </button>
            <button className="icon-btn" onClick={() => setShowMenu(!showMenu)}>
              <MoreVertIcon />
            </button>
            {showMenu && (
              <div className="dropdown-menu">
                <button onClick={() => { navigate("/budget-groups"); setShowMenu(false); }}>
                  Manage Budget Groups
                </button>
                <button onClick={signUserOut}>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card primary">
          <div className="card-header">
            <span>{getTimePeriodLabel(filterState.timePeriod)}</span>
            <span className="icon"><CalendarTodayIcon /></span>
          </div>
          <div className="card-amount">${totalSpending.toFixed(2)}</div>
        </div>
        <div className="summary-card secondary">
          <div className="card-header">
            <span>Transactions</span>
            <span className="icon"><ListAltIcon /></span>
          </div>
          <div className="card-amount">{filteredTransactions.length}</div>
        </div>
      </div>  

      {/* Settlement Card */}
      {settlementData && (
        <SettlementCard
          settlementData={settlementData}
          getBudgetGroupName={getBudgetGroupName}
          getUserFriendlyName={getUserFriendlyName}
        />
      )}

      {(showFilters) &&
      <div>
        <div className="filter-chips">
          <button
            className={`chip ${filterState.selectedCategory === null ? "active" : ""}`}
            onClick={() => setFilterState({ ...filterState, selectedCategory: null })}
          >
            {filterState.selectedCategory === null && "✓ "}All
          </button>
          {Object.values(TransactionCategory).map((category) => (
            <button
              key={category}
              className={`chip ${filterState.selectedCategory === category ? "active" : ""}`}
              onClick={() => setFilterState({ 
                ...filterState, 
                selectedCategory: filterState.selectedCategory === category ? null : category 
              })}
            >
              {filterState.selectedCategory === category && "✓ "}{category}
            </button>
          ))}
        </div>
        
        <div className="filter-chips">
          {Object.values(TimePeriod).map((period) => (
            <button
              key={period}
              className={`chip ${filterState.timePeriod === period ? "active" : ""}`}
              onClick={() => setFilterState({ ...filterState, timePeriod: period })}
            >
              {filterState.timePeriod === period && "✓ "}{getTimePeriodLabel(period)}
            </button>
          ))}
        </div>
        
        <div className="filter-chips">
          <button
            className={`chip ${filterState.selectedBudgetGroups.size === 0 ? "active" : ""}`}
            onClick={() => setFilterState({ ...filterState, selectedBudgetGroups: new Set() })}
          >
            {filterState.selectedBudgetGroups.size === 0 && "✓ "}All
          </button>
          {Object.values(budgetGroups).map((budgetGroup) => (
            <button
              key={budgetGroup.id}
              className={`chip ${filterState.selectedBudgetGroups.has(budgetGroup.id) ? "active" : ""}`}
              onClick={() => {
                const newGroups = new Set(filterState.selectedBudgetGroups);
                if (newGroups.has(budgetGroup.id)) {
                  newGroups.delete(budgetGroup.id);
                } else {
                  newGroups.add(budgetGroup.id);
                }
                setFilterState({ ...filterState, selectedBudgetGroups: newGroups });
              }}
            >
              {filterState.selectedBudgetGroups.has(budgetGroup.id) && "✓ "}{budgetGroup.friendlyName}
            </button>
          ))}
        </div>
      </div>
    }

      {/* Active Filters Display */}
      {(filterState.selectedCategory !== null || filterState.timePeriod !== TimePeriod.THIS_MONTH || filterState.selectedBudgetGroups.size > 0) && (
        <div className="active-filters">
          {filterState.selectedCategory !== null && (
            <div className="active-filter-chip">
              <span>{filterState.selectedCategory}</span>
              <button 
                className="remove-filter"
                onClick={() => setFilterState({ ...filterState, selectedCategory: null })}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </div>
          )}
          {filterState.timePeriod !== TimePeriod.THIS_MONTH && (
            <div className="active-filter-chip">
              <CalendarTodayIcon style={{ fontSize: 16 }} />
              <span>{getTimePeriodLabel(filterState.timePeriod)}</span>
              <button 
                className="remove-filter"
                onClick={() => setFilterState({ ...filterState, timePeriod: TimePeriod.THIS_MONTH })}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </div>
          )}
          {Array.from(filterState.selectedBudgetGroups).map((groupId) => (
            <div key={groupId} className="active-filter-chip">
              <GroupIcon style={{ fontSize: 16 }} />
              <span>{getBudgetGroupName(groupId) || groupId}</span>
              <button 
                className="remove-filter"
                onClick={() => {
                  const newGroups = new Set(filterState.selectedBudgetGroups);
                  newGroups.delete(groupId);
                  setFilterState({ ...filterState, selectedBudgetGroups: newGroups });
                }}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </div>
          ))}
          <button 
            className="clear-filters-btn"
            onClick={() => setFilterState({
              ...filterState,
              selectedCategory: null,
              timePeriod: TimePeriod.THIS_MONTH,
              selectedBudgetGroups: new Set()
            })}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Transactions List */}
      <div className="transactions-list">
        <TransactionList
          groupedTransactions={groupedTransactions}
          userProfileUserId={userProfile.userId}
          onEdit={handleEdit}
          onDelete={setDeleteConfirm}
          getUserFriendlyName={getUserFriendlyName}
          getBudgetGroupName={getBudgetGroupName}
        />
      </div>

      {/* FAB */}
      <button className="fab" onClick={() => setShowModal(true)}>
        <span className="fab-icon">+</span>
        <span className="fab-text">Add</span>
      </button>

      {/* Add/Edit Transaction Modal */}
      <TransactionModal
        isOpen={showModal}
        editingTransaction={transactionForm.editingTransaction}
        transactionAmount={transactionForm.transactionAmount}
        setTransactionAmount={transactionForm.setTransactionAmount}
        shopName={transactionForm.shopName}
        setShopName={transactionForm.setShopName}
        selectedCategory={transactionForm.selectedCategory}
        setSelectedCategory={transactionForm.setSelectedCategory}
        paidBy={transactionForm.paidBy}
        setPaidBy={transactionForm.setPaidBy}
        selectedBudgetGroupId={transactionForm.selectedBudgetGroupId}
        setSelectedBudgetGroupId={transactionForm.setSelectedBudgetGroupId}
        splitAmounts={transactionForm.splitAmounts}
        setSplitAmounts={transactionForm.setSplitAmounts}
        budgetGroupUsers={budgetGroupUsers}
        budgetGroups={budgetGroups}
        userProfileUserId={userProfile.userId}
        onSubmit={onSubmit}
        onClose={closeModal}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="Delete Transaction?"
        message={`Are you sure you want to delete this transaction from ${deleteConfirm?.shopName}?`}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />


    </div>
  );
};
